/**
 * Supervisor console — Sprint 32C · Feb 2026.
 *
 * Kanban board with columns per agent. Cards = active conversations.
 * - Drag & drop a card onto another column → POST /api/conversations/{cid}/reassign
 * - Click a card → open a modal with the LIVE feed of that conversation (WebSocket).
 *   The bot/agent DO NOT know the supervisor is watching (100% silent).
 * - Button "Take control" in the modal → sends the supervisor's first agent message,
 *   which triggers the normal "agent joined the conversation" flow.
 *
 * RBAC: page + route gated to admin | supervisor.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Topbar from "@/components/Topbar";
import { TeamTrainingPanel } from "@/components/TeamTrainingPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  UsersThree, Eye, ArrowRight, X as XIcon, PaperPlaneRight, Warning,
  ChatCircleDots, WhatsappLogo, InstagramLogo, FacebookLogo, Globe,
  CircleNotch, Circle, Clock, ArrowsClockwise, CaretDown,
  Star, GitMerge, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const CHANNEL_ICONS = {
  whatsapp: WhatsappLogo,
  instagram: InstagramLogo,
  messenger: FacebookLogo,
  web: Globe,
  email: ChatCircleDots,
};

const PRESENCE_TONE = {
  available: "bg-emerald-500",
  busy: "bg-amber-500",
  away: "bg-slate-400",
  offline: "bg-slate-500",
};

export default function Supervisor() {
  const { user } = useAuth();
  const { lang } = useLang();
  const t = (es, en) => (lang === "es" ? es : en);
  const token = typeof window !== "undefined" ? localStorage.getItem("sdm_token") : null;

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState(null);

  // Filters
  const [filterAgent, setFilterAgent] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUrgent, setFilterUrgent] = useState(false);

  // Drag-drop state
  const [dragging, setDragging] = useState(null); // {cid, fromAgentId}
  const [wsConnected, setWsConnected] = useState(false);

  const isAllowed = user && (user.role === "admin" || user.role === "supervisor");

  const loadBoard = async () => {
    try {
      const { data } = await api.get("/supervisor/kanban");
      setBoard(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => { if (isAllowed) loadBoard(); }, [isAllowed]);

  // WebSocket: live update of the board + modal feed
  const wsRef = useRef(null);
  useEffect(() => {
    if (!isAllowed || !token) return;
    const wsUrl = API_BASE.replace(/^http/, "ws") + `/ws/supervise?token=${token}`;
    let alive = true;
    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { if (alive) setWsConnected(true); };
      ws.onclose = () => {
        if (alive) setWsConnected(false);
        // Simple reconnect with 3s backoff
        if (alive) setTimeout(connect, 3000);
      };
      ws.onerror = () => {};
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "conversation" && ev.action === "reassigned") {
            loadBoard();
          } else if (ev.type === "message") {
            // Update card timestamp if visible + push into open modal
            setBoard((prev) => {
              if (!prev) return prev;
              const patch = (list) => list.map((c) =>
                c.id === ev.conversation_id
                  ? { ...c, last_message_preview: (ev.message?.content || c.last_message_preview || "").slice(0, 120), last_message_at: ev.ts }
                  : c
              );
              return {
                ...prev,
                unassigned: patch(prev.unassigned || []),
                columns: (prev.columns || []).map((col) => ({ ...col, conversations: patch(col.conversations || []) })),
              };
            });
            if (selectedConv?.id === ev.conversation_id) {
              setLiveMessages((cur) => [...cur, ev.message]);
            }
          }
        } catch (_) { /* ignore */ }
      };
    };
    connect();
    return () => {
      alive = false;
      try { wsRef.current?.close(); } catch (_) { /* ignore */ }
    };
  }, [isAllowed, token]);

  // Modal live feed state
  const [liveMessages, setLiveMessages] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const feedScrollRef = useRef(null);

  useEffect(() => {
    if (!selectedConv) { setLiveMessages([]); return; }
    setModalLoading(true);
    api.get(`/conversations/${selectedConv.id}`)
      .then((r) => setLiveMessages(r.data?.messages || []))
      .catch(() => setLiveMessages([]))
      .finally(() => setModalLoading(false));
  }, [selectedConv]);

  useEffect(() => {
    if (feedScrollRef.current) feedScrollRef.current.scrollTop = feedScrollRef.current.scrollHeight;
  }, [liveMessages]);

  // Filtered columns
  const displayColumns = useMemo(() => {
    if (!board) return [];
    const matches = (c) => {
      if (filterChannel && c.channel !== filterChannel) return false;
      if (filterStatus && c.status !== filterStatus) return false;
      if (filterUrgent && !c.sla_breach) return false;
      return true;
    };
    const cols = board.columns
      .filter((col) => !filterAgent || col.agent_id === filterAgent)
      .map((col) => ({ ...col, conversations: col.conversations.filter(matches) }));
    return cols;
  }, [board, filterAgent, filterChannel, filterStatus, filterUrgent]);

  const filteredUnassigned = useMemo(() => {
    if (!board) return [];
    return (board.unassigned || []).filter((c) => {
      if (filterAgent) return false; // unassigned column hidden when filtering by agent
      if (filterChannel && c.channel !== filterChannel) return false;
      if (filterStatus && c.status !== filterStatus) return false;
      if (filterUrgent && !c.sla_breach) return false;
      return true;
    });
  }, [board, filterAgent, filterChannel, filterStatus, filterUrgent]);

  const onDragStart = (e, card, fromAgentId) => {
    setDragging({ cid: card.id, fromAgentId });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", card.id);
  };

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const onDrop = async (e, targetAgentId) => {
    e.preventDefault();
    const cid = dragging?.cid || e.dataTransfer.getData("text/plain");
    const from = dragging?.fromAgentId;
    setDragging(null);
    if (!cid) return;
    if (from === targetAgentId) return;
    setBoard((b) => {
      if (!b) return b;
      const all = [...b.unassigned, ...b.columns.flatMap((c) => c.conversations)];
      const card = all.find((c) => c.id === cid);
      if (!card) return b;
      const strip = (list) => list.filter((c) => c.id !== cid);
      return {
        ...b,
        unassigned: targetAgentId ? strip(b.unassigned) : [card, ...strip(b.unassigned)],
        columns: b.columns.map((c) => ({
          ...c,
          conversations: c.agent_id === targetAgentId ? [card, ...strip(c.conversations)] : strip(c.conversations),
        })),
      };
    });
    try {
      await api.post(`/conversations/${cid}/reassign`, {
        target_agent_id: targetAgentId || null,
        reason: null,
        silent: false,
      });
      toast.success(t("Conversación reasignada", "Conversation reassigned"));
      await loadBoard();
      document.querySelector(`[data-testid="${targetAgentId ? `col-agent-${targetAgentId}` : "col-unassigned"}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
      loadBoard();
    }
  };

  const takeControl = async () => {
    if (!selectedConv) return;
    const msg = window.prompt(t("Escribe el mensaje inicial al cliente:", "Type your first message to the customer:"));
    if (!msg || !msg.trim()) return;
    try {
      await api.post(`/conversations/${selectedConv.id}/messages`, {
        content: msg.trim(),
        sender_type: "agent",
      });
      toast.success(t("Tomaste control de la conversación", "You took control of the conversation"));
      setSelectedConv(null);
      loadBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (!isAllowed) {
    return (
      <>
        <Topbar title={t("Panel de supervisión", "Supervisor Console")} />
        <div className="p-8 text-sm text-muted-foreground">
          {t("Acceso restringido a Admin y Supervisor.", "Restricted to Admin and Supervisor.")}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={t("Panel de supervisión", "Supervisor Console")}
        subtitle={t(
          "Monitoreo en vivo · arrastra tarjetas entre agentes para reasignar",
          "Live monitoring · drag cards between agents to reassign",
        )}
      />
      <div className="p-6 space-y-4 flex-1 min-h-0 flex flex-col overflow-y-auto [&>*]:shrink-0 animate-fade-in" data-testid="supervisor-root">
        {/* Connected agents panel */}
        <ConnectedAgentsPanel lang={lang} t={t} />

        {/* Training progress widget (Sprint 34O) */}
        <TeamTrainingPanel />

        {/* Sprint 35F — CSAT por representante + Historial de fusiones */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CsatByAgentPanel lang={lang} t={t} />
          <MergeHistoryPanel lang={lang} t={t} />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
          <span
            data-testid="supervisor-ws-status"
            className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border ${
              wsConnected
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600"
                : "border-slate-400/40 bg-slate-400/10 text-muted-foreground"
            }`}
          >
            <Circle size={8} weight="fill" />
            {wsConnected ? t("En vivo", "Live") : t("Reconectando…", "Reconnecting…")}
          </span>

          <select
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            className="text-xs h-7 rounded-md border border-input bg-background px-2"
            data-testid="supervisor-filter-agent"
          >
            <option value="">{t("Todos los agentes", "All agents")}</option>
            {(board?.columns || []).map((c) => (
              <option key={c.agent_id} value={c.agent_id}>{c.name}</option>
            ))}
          </select>

          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="text-xs h-7 rounded-md border border-input bg-background px-2"
            data-testid="supervisor-filter-channel"
          >
            <option value="">{t("Todos los canales", "All channels")}</option>
            <option value="web">Web</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="messenger">Messenger</option>
            <option value="instagram">Instagram</option>
            <option value="email">Email</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs h-7 rounded-md border border-input bg-background px-2"
            data-testid="supervisor-filter-status"
          >
            <option value="">{t("Todos los estados", "All statuses")}</option>
            <option value="open">{t("Abiertas", "Open")}</option>
            <option value="assigned">{t("Asignadas", "Assigned")}</option>
            <option value="escalated">{t("Escaladas", "Escalated")}</option>
          </select>

          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={filterUrgent}
              onChange={(e) => setFilterUrgent(e.target.checked)}
              data-testid="supervisor-filter-urgent"
            />
            <Warning size={12} className="text-amber-500" />
            {t("Solo urgentes (SLA)", "Only urgent (SLA)")}
          </label>

          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={loadBoard} data-testid="supervisor-refresh">
            <ArrowsClockwise size={12} className="mr-1" />
            {t("Refrescar", "Refresh")}
          </Button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            <CircleNotch size={20} className="animate-spin mr-2" /> {t("Cargando…", "Loading…")}
          </div>
        ) : (
          <div className="flex-1 min-h-[560px] overflow-x-auto" data-testid="supervisor-board">
            <div className="flex gap-3 h-[560px] pb-2">
              {/* Unassigned column */}
              {(!filterAgent) && (
                <KanbanColumn
                  title={t("Sin asignar", "Unassigned")}
                  subtitle={t("Pool de espera", "Waiting pool")}
                  count={filteredUnassigned.length}
                  agentId={null}
                  presence="offline"
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, null)}
                  emptyLabel={t("Sin conversaciones", "No conversations")}
                  testid="col-unassigned"
                >
                  {filteredUnassigned.map((c) => (
                    <ConvCard
                      key={c.id}
                      card={c}
                      onOpen={() => setSelectedConv(c)}
                      onDragStart={(e) => onDragStart(e, c, null)}
                      lang={lang}
                    />
                  ))}
                </KanbanColumn>
              )}

              {displayColumns.map((col) => (
                <KanbanColumn
                  key={col.agent_id}
                  title={col.name}
                  subtitle={col.role}
                  count={col.conversations.length}
                  agentId={col.agent_id}
                  presence={col.presence}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, col.agent_id)}
                  emptyLabel={t("Sin conversaciones", "No conversations")}
                  testid={`col-agent-${col.agent_id}`}
                >
                  {col.conversations.map((c) => (
                    <ConvCard
                      key={c.id}
                      card={c}
                      onOpen={() => setSelectedConv(c)}
                      onDragStart={(e) => onDragStart(e, c, col.agent_id)}
                      lang={lang}
                    />
                  ))}
                </KanbanColumn>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Silent monitoring modal */}
      {selectedConv && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="supervise-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedConv(null); }}
        >
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="min-w-0">
                <div className="font-heading font-bold text-sm flex items-center gap-2">
                  <Eye size={14} className="text-primary" />
                  {selectedConv.customer_name}
                  <Badge variant="outline" className="text-[10px] uppercase">{selectedConv.channel}</Badge>
                  {selectedConv.sla_breach && (
                    <Badge className="text-[10px] uppercase bg-red-500 text-white border-transparent">
                      SLA
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {t("Monitoreo silencioso · el agente no lo sabe", "Silent monitoring · agent unaware")}
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setSelectedConv(null)} data-testid="supervise-close">
                <XIcon size={14} />
              </Button>
            </div>
            <div
              ref={feedScrollRef}
              className="flex-1 overflow-y-auto p-4 space-y-2 bg-muted/20"
              data-testid="supervise-feed"
            >
              {modalLoading ? (
                <div className="text-center text-xs text-muted-foreground py-4">{t("Cargando…", "Loading…")}</div>
              ) : liveMessages.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-4">{t("Sin mensajes", "No messages")}</div>
              ) : (
                liveMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[80%] text-xs rounded-lg px-3 py-2 ${
                      m.sender_type === "customer"
                        ? "bg-primary/10 mr-auto"
                        : m.sender_type === "system"
                        ? "bg-muted mx-auto text-center text-muted-foreground text-[10px] italic"
                        : "bg-emerald-500/10 ml-auto"
                    }`}
                    data-testid={`supervise-msg-${m.sender_type}`}
                  >
                    {m.sender_type !== "system" && (
                      <div className="text-[9px] uppercase text-muted-foreground mb-0.5">
                        {m.sender_name || m.sender_type}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  </div>
                ))
              )}
            </div>
            <div className="p-3 border-t border-border flex items-center justify-between bg-card">
              <div className="text-[10px] text-muted-foreground">
                {t("El bot y el agente no reciben notificación de tu monitoreo.", "The bot and the agent are not notified.")}
              </div>
              <Button
                size="sm"
                onClick={takeControl}
                data-testid="supervise-take-control"
              >
                <PaperPlaneRight size={12} className="mr-1" />
                {t("Tomar control", "Take control")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------- Sub-components ----------
function KanbanColumn({ title, subtitle, count, agentId, presence, onDragOver, onDrop, children, emptyLabel, testid }) {
  const [isDropTarget, setIsDropTarget] = React.useState(false);
  return (
    <div
      className={`w-72 shrink-0 rounded-lg border ${isDropTarget ? "border-primary bg-primary/5" : "border-border bg-card"} flex flex-col`}
      onDragOver={(e) => { onDragOver(e); setIsDropTarget(true); }}
      onDragLeave={() => setIsDropTarget(false)}
      onDrop={(e) => { setIsDropTarget(false); onDrop(e); }}
      data-testid={testid}
    >
      <div className="p-3 border-b border-border flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${PRESENCE_TONE[presence] || PRESENCE_TONE.offline}`} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{title}</div>
          {subtitle && <div className="text-[10px] text-muted-foreground uppercase">{subtitle}</div>}
        </div>
        <Badge variant="outline" className="text-[10px]">{count}</Badge>
      </div>
      <div className="p-2 space-y-2 flex-1 overflow-y-auto min-h-[100px]">
        {React.Children.count(children) === 0 ? (
          <div className="text-[11px] text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">
            {emptyLabel}
          </div>
        ) : children}
      </div>
    </div>
  );
}

function ConvCard({ card, onOpen, onDragStart, lang }) {
  const Icon = CHANNEL_ICONS[card.channel] || Globe;
  const t = (es, en) => (lang === "es" ? es : en);
  const timeAgo = card.last_message_at ? new Date(card.last_message_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onOpen}
      data-testid={`conv-card-${card.id}`}
      className={`p-2.5 rounded-md border cursor-pointer hover:border-primary hover:shadow-sm transition-all ${
        card.sla_breach ? "border-red-500/60 bg-red-500/5" : "border-border bg-background"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} className="text-muted-foreground" />
        <span className="text-xs font-medium truncate flex-1">{card.customer_name}</span>
        {card.sla_breach && (
          <Warning size={12} weight="fill" className="text-red-500 shrink-0" title={t("SLA vencido", "SLA breached")} />
        )}
      </div>
      {card.last_message_preview && (
        <div className="text-[11px] text-muted-foreground line-clamp-2">
          {card.last_message_preview}
        </div>
      )}
      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
        {(card.tags || []).slice(0, 3).map((tag) => (
          <Badge key={tag} variant="outline" className="text-[9px] py-0 px-1">{tag}</Badge>
        ))}
        {timeAgo && (
          <span className="text-[9px] text-muted-foreground ml-auto flex items-center gap-0.5">
            <Clock size={9} /> {timeAgo}
          </span>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ConnectedAgentsPanel — Sprint 33E · Feb 2026
// ────────────────────────────────────────────────────────────────────────────
// Live roster of users grouped by presence (available · busy · away · offline).
// Migrated from the former "Agentes" section of Configuración. Refreshes
// every 20s so the supervisor sees who is actually online right now.
function ConnectedAgentsPanel({ lang, t }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent — supervisor already sees Kanban even if this fails
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  // Order presences: available → busy → away → offline
  const buckets = { available: [], busy: [], away: [], offline: [] };
  for (const u of users) {
    const p = buckets[u.presence] ? u.presence : "offline";
    buckets[p].push(u);
  }
  const connectedCount = buckets.available.length + buckets.busy.length + buckets.away.length;

  const PRESENCE_META = {
    available: { color: "bg-emerald-500", label: t("Disponible", "Available"), border: "border-emerald-500/40 bg-emerald-500/5" },
    busy:      { color: "bg-amber-500",   label: t("Ocupado", "Busy"),        border: "border-amber-500/40 bg-amber-500/5" },
    away:      { color: "bg-slate-400",   label: t("Ausente", "Away"),        border: "border-slate-400/40 bg-slate-400/5" },
    offline:   { color: "bg-slate-600",   label: t("Desconectado", "Offline"), border: "border-border" },
  };

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="rounded-lg border border-border bg-card overflow-hidden"
      data-testid="connected-agents-panel"
    >
      <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between gap-2 hover:bg-muted/40">
        <div className="flex items-center gap-2">
          <UsersThree size={14} className="text-primary" weight="fill" />
          <span className="text-xs font-semibold">{t("Agentes conectados", "Connected agents")}</span>
          <span className="text-[10px] text-muted-foreground">
            {loading ? "…" : t(
              `${connectedCount} en línea · ${buckets.offline.length} offline`,
              `${connectedCount} online · ${buckets.offline.length} offline`
            )}
          </span>
        </div>
        <CaretDown size={12} className={`transition-transform ${open ? "rotate-180" : ""} text-muted-foreground`} />
      </summary>
      <div className="px-3 pb-3 pt-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
        {(["available", "busy", "away", "offline"]).map((p) => {
          const meta = PRESENCE_META[p];
          const bucket = buckets[p];
          return (
            <div key={p} className={`rounded-md border ${meta.border} p-2 min-h-[80px]`} data-testid={`connected-bucket-${p}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`w-2 h-2 rounded-full ${meta.color}`} />
                <span className="text-[10px] uppercase tracking-wider font-semibold">
                  {meta.label} · {bucket.length}
                </span>
              </div>
              {bucket.length === 0 ? (
                <div className="text-[10px] text-muted-foreground py-2">
                  {t("Sin agentes", "No agents")}
                </div>
              ) : (
                <ul className="space-y-1">
                  {bucket.slice(0, 8).map((u) => (
                    <li key={u.id} className="flex items-center gap-1.5 text-[11px]" data-testid={`connected-agent-${u.id}`}>
                      <div className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[9px] font-bold shrink-0">
                        {(u.name || "?")[0]}
                      </div>
                      <span className="truncate">{u.name}</span>
                      <span className="text-[9px] text-muted-foreground ml-auto uppercase shrink-0">{u.role}</span>
                    </li>
                  ))}
                  {bucket.length > 8 && (
                    <li className="text-[9px] text-muted-foreground pl-6">+{bucket.length - 8} {t("más", "more")}</li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}





// ============================================================================
// Sprint 35F — CSAT por representante (bar chart)
// ============================================================================
function CsatByAgentPanel({ lang, t }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState({ rows: [], total_ratings: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/analytics/csat/by-agent?range=${range}`)
      .then((r) => { if (alive) setData(r.data || { rows: [], total_ratings: 0 }); })
      .catch(() => { if (alive) setData({ rows: [], total_ratings: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range]);

  const chartRows = (data.rows || []).slice(0, 12).map((r) => ({
    name: (r.agent_name || "").split(" ").slice(0, 2).join(" "),
    score: r.avg_score,
    count: r.count,
    agent_id: r.agent_id,
  }));

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="csat-by-agent-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star size={16} weight="fill" className="text-amber-500" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider">
            {lang === "es" ? "CSAT por representante" : "CSAT by agent"}
          </h3>
          <span className="text-[10px] text-muted-foreground">
            · {data.total_ratings} {lang === "es" ? "encuestas" : "surveys"}
          </span>
        </div>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          data-testid="csat-range-select"
          className="text-xs h-7 rounded-md border border-input bg-background px-2"
        >
          <option value="7d">{lang === "es" ? "7 días" : "7 days"}</option>
          <option value="30d">{lang === "es" ? "30 días" : "30 days"}</option>
          <option value="90d">{lang === "es" ? "90 días" : "90 days"}</option>
        </select>
      </div>
      {loading ? (
        <div className="h-56 flex items-center justify-center">
          <CircleNotch size={18} className="animate-spin text-muted-foreground" />
        </div>
      ) : chartRows.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-xs text-muted-foreground">
          {lang === "es" ? "Sin encuestas CSAT en este rango." : "No CSAT ratings in this range."}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartRows} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
            <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload;
                return (
                  <div className="rounded-md border border-border bg-background p-2 text-xs shadow-md">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-amber-600 font-bold">⭐ {row.score} / 5</div>
                    <div className="text-muted-foreground">
                      {row.count} {lang === "es" ? "encuestas" : "surveys"}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="score" radius={[4, 4, 0, 0]} fill="#F59E0B" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ============================================================================
// Sprint 35F — Historial de fusiones de contactos
// ============================================================================
function MergeHistoryPanel({ lang, t }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/merge-log?limit=50");
      setRows(data || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const canReverse = user?.role === "admin";

  const reverse = async (id, name) => {
    if (!window.confirm(
      lang === "es"
        ? `¿Revertir la fusión del contacto "${name}"? Se restaurará el contacto fuente y sus conversaciones.`
        : `Reverse the merge for "${name}"? Source contact will be recreated and conversations restored.`,
    )) return;
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/merge-log/${id}/reverse`);
      toast.success(
        lang === "es"
          ? `Revertida · ${data.conversations_moved_back} conversaciones devueltas`
          : `Reversed · ${data.conversations_moved_back} conversations moved back`,
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="merge-history-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GitMerge size={16} weight="bold" className="text-primary" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider">
            {lang === "es" ? "Historial de fusiones" : "Merge history"}
          </h3>
          <span className="text-[10px] text-muted-foreground">· {rows.length}</span>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="merge-history-refresh"
          className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1"
          title={lang === "es" ? "Actualizar" : "Refresh"}
        >
          <ArrowsClockwise size={12} /> {lang === "es" ? "Actualizar" : "Refresh"}
        </button>
      </div>
      {loading ? (
        <div className="h-56 flex items-center justify-center">
          <CircleNotch size={18} className="animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-xs text-muted-foreground">
          {lang === "es" ? "Aún no se han fusionado contactos." : "No merges yet."}
        </div>
      ) : (
        <div className="max-h-72 overflow-auto divide-y divide-border">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`merge-log-${r.id}`}
              className="py-2 text-xs flex items-start justify-between gap-2"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">
                    {r.type === "customer_merge"
                      ? (r.source_snapshot?.name || "—")
                      : (lang === "es"
                          ? `Dedup masivo: ${r.customers_affected?.length || 0} clientes`
                          : `Bulk dedup: ${r.customers_affected?.length || 0} customers`)}
                  </span>
                  {r.type === "customer_merge" && r.primary_name && (
                    <>
                      <ArrowRight size={10} className="text-muted-foreground" />
                      <span className="truncate text-muted-foreground">{r.primary_name}</span>
                    </>
                  )}
                  {r.reversed_at && (
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 text-[9px] uppercase">
                      {lang === "es" ? "revertida" : "reversed"}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {r.actor_name} · {new Date(r.performed_at).toLocaleString()}
                  {r.report?.messages_reassigned != null && (
                    <> · {r.report.messages_reassigned} msgs · {r.report.conversations_reassigned ?? r.report.conversations_removed ?? 0} conv</>
                  )}
                </div>
              </div>
              {canReverse && r.type === "customer_merge" && !r.reversed_at && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => reverse(r.id, r.source_snapshot?.name || r.source_id)}
                  data-testid={`merge-log-reverse-${r.id}`}
                  className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 disabled:opacity-50"
                >
                  <ArrowCounterClockwise size={11} />
                  {busyId === r.id ? "…" : (lang === "es" ? "Revertir" : "Reverse")}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

